import { useCallback, useDeferredValue, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DynamicAutocomplete from 'components/DynamicAutocomplete';
import { useCustomers } from 'contexts/Customers';
import type { ICustomer } from 'contexts/Customers';
import { useDebouncedCallback } from 'use-debounce';
import { getCustomerLabel } from 'utils';

import type { ICustomers } from './types';

const startSearchChar: number = 3;

export default function Customers({
  disabled,
  textFieldProps,
  selectedCustomer,
  onChange = () => {},
  onClose = () => {},
  ...autoProps
}: ICustomers<ICustomer>) {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const handleOnClose = useCallback(() => {
    setOpen(false);
    onClose();
  }, [onClose]);

  const handleInputChange = useDebouncedCallback((event, value, reason) => {
    if (reason !== 'reset') setSearchTerm(value);

    if (value.length >= startSearchChar && reason === 'input') {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, 500);

  const handleOnChange = useCallback(
    (event, value) => {
      onChange(value);
    },
    [onChange],
  );

  const {
    state: { loading, customers = [] },
    actions: { loadCustomers },
  } = useCustomers();

  useEffect(() => {
    if (deferredSearchTerm.length >= startSearchChar) {
      loadCustomers({ limit: 200, q: deferredSearchTerm });
    }
  }, [deferredSearchTerm, loadCustomers]);

  const getOptionLabel = (option) => {
    return getCustomerLabel(option);
  };

  return (
    <DynamicAutocomplete
      {...autoProps}
      disabled={disabled}
      textFieldProps={{
        label: t('label.accountNumber'),
        placeholder: t('placeholders.accountNumber'),
        value: searchTerm,
        name: 'accountNumber',
        ...textFieldProps,
      }}
      noOptionsText={t('label.weCouldNot')}
      open={open}
      onClose={handleOnClose}
      value={selectedCustomer}
      options={customers}
      onInputChange={handleInputChange}
      getOptionLabel={getOptionLabel}
      onChange={handleOnChange}
      loading={loading}
      isOptionEqualToValue={(option, value) =>
        option.customerReference === (typeof value === 'string' ? value : value?.customerReference)
      }
    />
  );
}
